<script setup lang="ts">
    import { reactive, ref } from 'vue';
    import Loading from '@shared/components/Loading.vue';
    import List from '@shared/components/List/List.vue';
    import Modal from '@shared/components/Modal.vue';
    import ConfirmButton from '@shared/components/ConfirmButton.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import { formatDateTime } from '@shared/utils/formatDateTime';
    import ConnectionForm from './ConnectionForm.vue';
    import { getConnections, getConnectionTypes, deleteConnection, type ConnectionSummary, type ConnectionType } from './AccountConnections.access';

    const state = reactive<{
        loading: boolean;
        loadError: boolean;
        connections: ConnectionSummary[];
        connectionTypes: ConnectionType[];
        showDialog: boolean;
        editingConnection: ConnectionSummary | null;
        busyConnectionIds: Set<string>;
    }>({
        loading: true,
        loadError: false,
        connections: [],
        connectionTypes: [],
        showDialog: false,
        editingConnection: null,
        busyConnectionIds: new Set(),
    });

    const connectionForm = ref<InstanceType<typeof ConnectionForm> | null>(null);

    (async () => {
        try {
            state.connections = await getConnections();
        } catch {
            state.loadError = true;
        } finally {
            state.loading = false;
        }
    })();

    function openAddDialog(): void {
        state.editingConnection = null;
        state.showDialog = true;
        ensureConnectionTypesLoaded();
    }

    async function ensureConnectionTypesLoaded(): Promise<void> {
        if (state.connectionTypes.length) return;
        state.connectionTypes = await getConnectionTypes();
    }

    function edit(connection: ConnectionSummary): void {
        state.editingConnection = connection;
        state.showDialog = true;
        ensureConnectionTypesLoaded();
    }

    function onCreated(connection: ConnectionSummary): void {
        state.connections.unshift(connection);
        state.showDialog = false;
    }

    const tableHeaders = [
        $Resources.Name,
        $Resources.GenericCreatedLabel,
        $Resources.LastModifiedLabel,
        $Resources.GenericModifiedByLabel,
        $Resources.RemoveButton,
    ];

    function onSaved(connection: ConnectionSummary): void {
        const index = state.connections.findIndex((c) => c.ConnectionId === connection.ConnectionId);
        if (index > -1) state.connections[index] = connection;
        state.showDialog = false;
    }

    async function onDelete(connection: ConnectionSummary): Promise<void> {
        state.busyConnectionIds.add(connection.ConnectionId);

        try {
            await deleteConnection(connection.ConnectionId);
            state.connections = state.connections.filter((c) => c.ConnectionId !== connection.ConnectionId);
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            state.busyConnectionIds.delete(connection.ConnectionId);
        }
    }
</script>

<template>
    <h1 class="h2 m-lg-b">{{ $Resources.ManageConnections }}</h1>

    <Loading :show="state.loading" loadingStyle="progress-bar" fixed></Loading>

    <BsAlert v-if="state.loadError" :type="AlertTypes.Danger" :dismissible="false" :title="$Resources.ErrorHeading" :content="$Resources.ErrorContactSupport" />

    <template v-if="!state.loading && !state.loadError">
        <List :items="state.connections" itemId="ConnectionId" orderByValue="Name" displayAs="table" :tableHeaders="tableHeaders" hideSearch>
            <template #toolbar>
                <div v-backoffice>
                    <button type="button" class="btn btn-primary" @click="openAddDialog">
                        {{ $Resources.ManageConnectionsAddConnectionButton }}
                    </button>
                </div>
            </template>
            <template #item="{ item: connection }">
                <td>
                    <a href="javascript:void(0)" role="button" @click="edit(connection)">{{ connection.Name }}</a>
                    <br />
                    <small>{{ connection.Type }}</small>
                </td>
                <td>{{ formatDateTime(connection.Created) }}</td>
                <td>{{ formatDateTime(connection.Modified) }}</td>
                <td>{{ connection.ModifiedBy }}</td>
                <td class="text-right">
                    <ConfirmButton
                        buttonClasses="btn-sm"
                        :buttonText="$Resources.DeleteButtonDelete"
                        :disabled="state.busyConnectionIds.has(connection.ConnectionId)"
                        @confirm="onDelete(connection)"
                        undoButton></ConfirmButton>
                </td>
            </template>
        </List>
    </template>

    <Modal
        size="sm"
        :show="state.showDialog"
        :title="state.editingConnection ? $Resources.EditConnectionHeader : $Resources.CreateConnectionHeader"
        @close="state.showDialog = false">
        <template #content>
            <ConnectionForm
                ref="connectionForm"
                :connectionTypes="state.connectionTypes"
                :connection="state.editingConnection"
                @saved="state.editingConnection ? onSaved($event) : onCreated($event)"></ConnectionForm>
        </template>
        <template #button>
            <button
                v-if="connectionForm?.hasSelectedType"
                type="button"
                class="btn btn-primary"
                :class="{ 'is-busy': connectionForm.saving }"
                :disabled="!connectionForm.saveEnabled"
                @click="connectionForm.save()">
                {{ $Resources.SaveBtn }}
            </button>
        </template>
    </Modal>
</template>
